// Area-layer tiles kept on the phone (2026-10-08, areaTiles.ts): memory for
// this session, IndexedDB across sessions so a crew reopening the app in the
// same street sees the faint Area layer at once, and offline.
//
// Keyed by the signed-in user and the data profile: a shared phone never
// shows one organisation's tiles to another login. Entries older than
// TILE_TTL_MS are refetched (pipeline re-runs change the data).
import { AREA_TILES, tx } from "./outbox";
import type { AreaAsset } from "./fieldApi";

export type Road = { name: string; path: [number, number][] };
export interface StoredTile {
  key: string;
  owner: string;
  at: number;
  assets: AreaAsset[];
  roads: Road[];
  coarse?: boolean;
}

export const TILE_TTL_MS = 24 * 3600 * 1000;
const MEM_MAX = 2500;
const mem = new Map<string, StoredTile>();

function full(owner: string, key: string): string {
  return `${owner}|${key}`;
}

export function fresh(t: StoredTile | undefined, now = Date.now()): t is StoredTile {
  return !!t && now - t.at < TILE_TTL_MS;
}

export function memGet(owner: string, key: string): StoredTile | undefined {
  const t = mem.get(full(owner, key));
  return fresh(t) ? t : undefined;
}

export function memPut(owner: string, t: Omit<StoredTile, "owner" | "key"> & { key: string }): StoredTile {
  const st: StoredTile = { ...t, owner };
  const k = full(owner, t.key);
  mem.delete(k);
  mem.set(k, st);
  while (mem.size > MEM_MAX) mem.delete(mem.keys().next().value as string);
  return st;
}

/** Load the tiles for these keys from IndexedDB into memory (skips what is
 * already there); returns how many were found fresh. Never throws. */
export async function hydrate(owner: string, keys: string[]): Promise<number> {
  const missing = keys.filter((k) => !memGet(owner, k));
  if (!missing.length) return 0;
  let n = 0;
  try {
    const got = await Promise.all(missing.map((k) => tx<StoredTile>(AREA_TILES, "readonly", (s) => s.get(full(owner, k)))));
    for (const t of got) {
      if (!fresh(t)) continue;
      const k = t.key.slice(owner.length + 1); // stored as "<owner>|<z/x/y>"
      mem.set(full(owner, k), { ...t, key: k });
      n++;
    }
  } catch {
    /* IndexedDB unavailable (private mode): memory only */
  }
  return n;
}

/** Persist tiles (fire and forget). */
export async function persist(owner: string, tiles: StoredTile[]): Promise<void> {
  if (!tiles.length) return;
  try {
    await tx(AREA_TILES, "readwrite", (s) => {
      for (const t of tiles) s.put({ ...t, key: full(owner, t.key) });
    });
  } catch {
    /* quota / private mode: memory only */
  }
}

/** Drop expired tiles (any owner). Never throws. */
export async function pruneTiles(now = Date.now()): Promise<number> {
  try {
    const all = (await tx<StoredTile[]>(AREA_TILES, "readonly", (s) => s.getAll())) ?? [];
    const old = all.filter((t) => now - t.at >= TILE_TTL_MS).map((t) => t.key);
    if (old.length) await tx(AREA_TILES, "readwrite", (s) => { for (const k of old) s.delete(k); });
    return old.length;
  } catch {
    return 0;
  }
}

/** Forget everything (sign-out). */
export async function clearTiles(): Promise<void> {
  mem.clear();
  try {
    await tx(AREA_TILES, "readwrite", (s) => s.clear());
  } catch {
    /* nothing stored */
  }
}
